import {
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  Dispatch,
} from 'redux';
import { AxiosError } from 'axios';
import {
  VseAlertState,
} from './alertTypes';
import {
  DotNetValidationProblemDetails,
} from 'vse-material-ui';
import { ApplicationState, useTypedSelector } from '../../store';
import * as VseSession from '../session';

// Private type from redux toolkit (not exported)
// Copied from https://github.com/reduxjs/redux-toolkit/blob/master/src/createAsyncThunk.ts
// Note: getState instead of state, because of the fact that it is getState and no state
type AsyncThunkConfig = {
  getState: () => unknown,
  dispatch: Dispatch,
  extra?: unknown,
  rejectValue?: unknown,
  serializedErrorType?: unknown,
};

const initialState = {
  successMessages: [],
  validationProblemMessages: [],
  serverErrorMessages: [],
} as VseAlertState;

export function tryHandleTimeout(thunkApi: AsyncThunkConfig, error: AxiosError) {
  if (error.response) {
    const state = thunkApi.getState() as ApplicationState;
    switch (error.response.status) {
      case 401:
        if (state.session.token.isAuthenticated) {
          thunkApi.dispatch(VseSession.timeout());
        }
        return true;
    }
  }
  return false;
}

export function handleThunkError(thunkApi: AsyncThunkConfig, error: AxiosError, msg404: string = 'Not found or access denied') {
  if (error.response) {
    switch (error.response.status) {
      case 400:
        thunkApi.dispatch(alertSlice.actions.alertValidationProblem(error.response.data));
        break;

      case 401:
        // Should be handled by tryHandleTimeout
        break;

      case 403:
      case 404:
        thunkApi.dispatch(alertSlice.actions.alertNotFound(msg404));
        break;

      default:
        thunkApi.dispatch(alertSlice.actions.alertServerError(error));
        break;
    }
  }
}

export const alertSlice = createSlice({
  name: 'alert',
  initialState,
  reducers: {
    reset: (state) => {
      state = Object.assign(state, initialState);
    },
    alertSucceeded: (state, action: PayloadAction<string>) => {
      state.successMessages.push(action.payload);
    },
    dismissSucceeded: (state, action: PayloadAction<string>) => {
      state.successMessages = state.successMessages.filter(s => s !== action.payload);
    },
    alertNotFound: (state, action: PayloadAction<string>) => {
      //TODO: not use validation problem messages
      state.validationProblemMessages = [action.payload];
    },
    alertValidationProblem: (state, action: PayloadAction<DotNetValidationProblemDetails>) => {
      state.validationProblemMessages = [];
      for (const p in action.payload.errors) {
        action.payload.errors[p]!.forEach(s => {
          state.validationProblemMessages.push(s);
        });
      }
    },
    alertValidationProblemMessages: (state, action: PayloadAction<string[]>) => {
      state.validationProblemMessages = [];
      action.payload.forEach(s => {
        state.validationProblemMessages.push(s);
      });
    },
    dismissValidationProblem: (state, action: PayloadAction<string[] | undefined>) => {
      if (action.payload) {
        const payload = action.payload;
        state.validationProblemMessages = state.validationProblemMessages.filter(s => payload.indexOf(s) === -1);
      } else {
        state.validationProblemMessages = [];
      }
    },
    alertServerError: (state, action: PayloadAction<AxiosError>) => {
      if (action.payload.response) {
        state.serverErrorMessages.push(action.payload.response.data);
      } else {
        state.serverErrorMessages.push("Unknown server error");
      }
    },
    dismissServerError: (state, action: PayloadAction<string>) => {
      state.serverErrorMessages = state.serverErrorMessages.filter(s => s !== action.payload);
    },
  },
});

export const {
  reset,
  alertSucceeded, dismissSucceeded,
  alertValidationProblem, dismissValidationProblem, alertValidationProblemMessages,
  alertServerError, dismissServerError,
} = alertSlice.actions;

export default alertSlice.reducer;

export const useVseAlertState = (): VseAlertState => useTypedSelector(state => state.alert);
